/**
 * Kundeliste v2 (VIB-2891). Ren kalkylefil: bygger én rad per kunde av det
 * kundekort v3 allerede regner ut (status, eiere, neste steg, GEEK i pipe,
 * GEEK levert, siste kontakt), grupperer radene etter hva som haster og
 * teller opp tellerne øverst. Ingen ny formel: GEEK i pipe er
 * `calcOfferTotals().bf` vektet med `weightedPipeline`, GEEK levert er
 * `customerGeek().deliveredKr` på prosjekter avsluttet siste 12 måneder.
 */
import { customerGeek, daysUntil } from "./customer-kpi-calc";
import { CUSTOMER_STATUS_ORDER, type CustomerStatus } from "./customer-status";
import { osloWeekBounds } from "./follow-up-calc";
import { formatDateShort } from "./format";
import { calcOfferTotals, type OfferForCalc, type OfferLineForCalc } from "./offer-calc";
import { isSalesProject } from "./offer-closed-date";
import { weightedPipeline, type PipelineOffer, type ProbabilityWeights } from "./offer-pipeline-calc";
import { OPEN_STAGES } from "./opportunity-calc";
import type {
  CustomerActivityType,
  DBCustomer,
  DBCustomerStatusRow,
  DBFollowUp,
  DBOpportunity,
  DBProject,
  FollowUpChannel,
  FollowUpSource,
} from "./db-types";

/** Hvor mange dager fram «Kjølner innen 30 d» ser. */
export const COOLING_WINDOW_DAYS = 30;
/** «GEEK levert 12 mnd» teller prosjekter avsluttet de siste 12 månedene. */
export const DELIVERED_WINDOW_MONTHS = 12;

export type UrgencyGroup = "overdue" | "this_week" | "cooling" | "calm";

export const URGENCY_ORDER: readonly UrgencyGroup[] = ["overdue", "this_week", "cooling", "calm"];

export const URGENCY_LABELS: Record<UrgencyGroup, string> = {
  overdue: "Forfalt",
  this_week: "Denne uken",
  cooling: "Kjølner innen 30 dager",
  calm: "Rolig",
};

export type CustomerListOwnerFilter = "all" | "mine" | "none";
/** Tellerne øverst er også filtre; `pipe` = har GEEK i pipe. */
export type CustomerListCounterFilter = "overdue" | "this_week" | "cooling" | "pipe";
/** Kolonnene som kan sorteres ved klikk på overskriften. */
export type CustomerListSortKey = "name" | "status" | "next_step" | "pipe" | "delivered" | "last_contact";

export interface CustomerListSort {
  key: CustomerListSortKey;
  dir: "asc" | "desc";
}

/** Første klikk på en kolonne: tall synkende, tekst og datoer stigende. */
export const DEFAULT_SORT_DIR: Record<CustomerListSortKey, "asc" | "desc"> = {
  name: "asc",
  status: "asc",
  next_step: "asc",
  pipe: "desc",
  delivered: "desc",
  last_contact: "desc",
};

export interface ListOwner {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

export interface ListNextStep {
  id: string;
  title: string;
  dueDate: string;
  /** Hele dager til forfall; negativt = forfalt. */
  dueInDays: number;
  channel: FollowUpChannel | null;
  source: FollowUpSource;
  ruleKey: string | null;
}

export interface ListLastContact {
  at: string;
  /** Hele dager siden kontakten. */
  daysAgo: number;
  type: CustomerActivityType;
  userName: string | null;
}

export interface CustomerListRow {
  id: string;
  name: string;
  logoUrl: string | null;
  countryCode: string | null;
  /** «Reklamebyrå · Bergen» fra Brønnøysund, null uten bedriftsinfo. */
  industry: string | null;
  municipality: string | null;
  status: CustomerStatus | null;
  isOverridden: boolean;
  coolsAt: string | null;
  /** Dager til kunden kjølner (bare for varme), ellers null. */
  coolsInDays: number | null;
  owners: ListOwner[];
  nextStep: ListNextStep | null;
  /** Vektet GEEK estimert på kundens sendte, gyldige tilbud. */
  pipeKr: number;
  pipeOfferCount: number;
  /** Åpne prosesser (Outreach, Møte, Tilbud). */
  openProcessCount: number;
  /** GEEK reell på prosjekter avsluttet siste 12 mnd. null uten tall. */
  deliveredKr: number | null;
  deliveredCount: number;
  projectCount: number;
  lastContact: ListLastContact | null;
  group: UrgencyGroup;
  /** Alt søket treffer på, i små bokstaver. */
  searchText: string;
}

export type ListCustomer = Pick<
  DBCustomer,
  "id" | "name" | "organization_number" | "contact_person" | "contact_email" | "invoice_email_address" | "country_code"
> & { logo_url?: string | null };

export type ListProject = Pick<DBProject, "id" | "customer_id" | "total_amount" | "bf_estimated" | "bf_actual" | "created_at"> & {
  is_internal: boolean | null;
  project_status: string | null;
  completed_at: string | null;
};

/** Sendt tilbud med det `calcOfferTotals` (GEEK) og `weightedPipeline` (vekt) trenger. */
export type ListOffer = PipelineOffer & OfferForCalc & { customer_id: string | null };
export type ListOfferLine = OfferLineForCalc & { offer_id: string };
export type ListFollowUp = Pick<DBFollowUp, "id" | "customer_id" | "title" | "due_date" | "channel" | "source" | "rule_key">;
export type ListOpportunity = Pick<DBOpportunity, "customer_id" | "stage">;

export interface ListLastContactRow {
  customer_id: string;
  last_contact_at: string;
  last_contact_type: CustomerActivityType;
  last_contact_user_id: string | null;
}

export interface ListCompanyInfo {
  customer_id: string;
  industry_description: string | null;
  municipality: string | null;
}

export interface ListOwnerRow {
  customer_id: string;
  user_id: string;
  user: { id: string; full_name: string; avatar_url: string | null } | null;
}

export interface BuildCustomerListInput {
  customers: readonly ListCustomer[];
  statuses: readonly DBCustomerStatusRow[];
  owners: readonly ListOwnerRow[];
  projects: readonly ListProject[];
  /** Kundens sendte tilbud (status `sent`); andre statuser ignoreres. */
  offers: readonly ListOffer[];
  offerLines: readonly ListOfferLine[];
  weights: ProbabilityWeights;
  openFollowUps: readonly ListFollowUp[];
  opportunities: readonly ListOpportunity[];
  lastContacts: readonly ListLastContactRow[];
  companyInfo: readonly ListCompanyInfo[];
  /** Navn per bruker-id, til «Siste kontakt». */
  userNames: ReadonlyMap<string, string>;
  today: Date;
}

function groupBy<T>(rows: readonly T[], key: (row: T) => string | null | undefined): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    if (!k) continue;
    const bucket = map.get(k);
    if (bucket) bucket.push(row);
    else map.set(k, [row]);
  }
  return map;
}

/** Dato (YYYY-MM-DD) 12 måneder før `today`. */
export function deliveredWindowStart(today: Date): string {
  const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - DELIVERED_WINDOW_MONTHS, today.getUTCDate()));
  return d.toISOString().slice(0, 10);
}

/**
 * GEEK i pipe per kunde: samme regnestykke som `useCustomerOfferGeek` på
 * kundekortet, bare for alle kunder på én gang.
 */
export function geekInPipeByCustomer(
  offers: readonly ListOffer[],
  lines: readonly ListOfferLine[],
  weights: ProbabilityWeights,
  today: Date,
): Map<string, { weightedKr: number; offerCount: number }> {
  const linesByOffer = groupBy(lines, (l) => l.offer_id);
  const sentByCustomer = groupBy(
    offers.filter((o) => o.status === "sent"),
    (o) => o.customer_id,
  );
  const result = new Map<string, { weightedKr: number; offerCount: number }>();
  for (const [customerId, sent] of sentByCustomer) {
    const geekOffers = sent.map((o) => ({ ...o, total_amount: calcOfferTotals(o, linesByOffer.get(o.id) ?? []).bf }));
    result.set(customerId, {
      weightedKr: weightedPipeline(geekOffers, weights, today).weightedKr,
      offerCount: sent.length,
    });
  }
  return result;
}

/** Eldste åpne oppfølging per kunde (forfall, så id for stabil rekkefølge). */
export function nextStepByCustomer(followUps: readonly ListFollowUp[], today: Date): Map<string, ListNextStep> {
  const result = new Map<string, ListNextStep>();
  const sorted = [...followUps].sort((a, b) => a.due_date.localeCompare(b.due_date) || a.id.localeCompare(b.id));
  for (const f of sorted) {
    if (result.has(f.customer_id)) continue;
    result.set(f.customer_id, {
      id: f.id,
      title: f.title,
      dueDate: f.due_date,
      dueInDays: daysUntil(f.due_date, today),
      channel: f.channel,
      source: f.source,
      ruleKey: f.rule_key,
    });
  }
  return result;
}

/**
 * Hvilken gruppe kunden hører til: forfalt neste steg, neste steg denne uka
 * (til og med søndag, Oslo-tid), varm kunde som kjølner innen 30 dager, ellers
 * rolig. Forfalt og denne uken trumfer kjølning.
 */
export function urgencyGroup(
  row: Pick<CustomerListRow, "status" | "coolsInDays" | "nextStep">,
  today: Date,
): UrgencyGroup {
  if (row.nextStep) {
    if (row.nextStep.dueInDays < 0) return "overdue";
    if (row.nextStep.dueDate <= osloWeekBounds(today).endDate) return "this_week";
  }
  if (row.status === "warm" && row.coolsInDays != null && row.coolsInDays >= 0 && row.coolsInDays <= COOLING_WINDOW_DAYS) {
    return "cooling";
  }
  return "calm";
}

/** Bygger én rad per kunde. Rekkefølgen er kundenes egen; sortering skjer i `sortCustomerRows`. */
export function buildCustomerListRows(input: BuildCustomerListInput): CustomerListRow[] {
  const { today } = input;
  const statusById = new Map(input.statuses.map((s) => [s.customer_id, s]));
  const ownersById = groupBy(input.owners, (o) => o.customer_id);
  const projectsById = groupBy(input.projects, (p) => p.customer_id);
  const pipeById = geekInPipeByCustomer(input.offers, input.offerLines, input.weights, today);
  const nextById = nextStepByCustomer(input.openFollowUps, today);
  const openById = groupBy(
    input.opportunities.filter((o) => OPEN_STAGES.includes(o.stage)),
    (o) => o.customer_id,
  );
  const contactById = new Map(input.lastContacts.map((c) => [c.customer_id, c]));
  const infoById = new Map(input.companyInfo.map((c) => [c.customer_id, c]));
  const since = deliveredWindowStart(today);

  return input.customers.map((c) => {
    const status = statusById.get(c.id) ?? null;
    const projects = projectsById.get(c.id) ?? [];
    const sales = projects.filter((p) => isSalesProject(p) && p.project_status !== "cancelled");
    const deliveredWindow = sales.filter((p) => p.project_status === "completed" && (p.completed_at ?? "") >= since);
    const geek = customerGeek(
      deliveredWindow.map((p) => ({ ...p, project_status: p.project_status ?? "", estimated_deadline: null })),
      new Map(),
    );
    const pipe = pipeById.get(c.id);
    const owners = (ownersById.get(c.id) ?? []).map<ListOwner>((o) => ({
      userId: o.user_id,
      name: o.user?.full_name ?? "",
      avatarUrl: o.user?.avatar_url ?? null,
    }));
    const contact = contactById.get(c.id);
    const info = infoById.get(c.id);
    const coolsInDays = status?.effective_status === "warm" && status.cools_at ? daysUntil(status.cools_at, today) : null;
    const nextStep = nextById.get(c.id) ?? null;
    const openProcessCount = openById.get(c.id)?.length ?? 0;
    const partial = { status: status?.effective_status ?? null, coolsInDays, nextStep };

    return {
      id: c.id,
      name: c.name,
      logoUrl: c.logo_url ?? null,
      countryCode: c.country_code ?? null,
      industry: info?.industry_description ?? null,
      municipality: info?.municipality ?? null,
      status: partial.status,
      isOverridden: status?.is_overridden ?? false,
      coolsAt: status?.cools_at ?? null,
      coolsInDays,
      owners,
      nextStep,
      pipeKr: pipe?.weightedKr ?? 0,
      pipeOfferCount: pipe?.offerCount ?? 0,
      openProcessCount,
      deliveredKr: geek.deliveredKr,
      deliveredCount: geek.deliveredCount,
      projectCount: sales.length,
      lastContact: contact
        ? {
          at: contact.last_contact_at,
          daysAgo: Math.max(0, -daysUntil(contact.last_contact_at, today)),
          type: contact.last_contact_type,
          userName: contact.last_contact_user_id ? input.userNames.get(contact.last_contact_user_id) ?? null : null,
        }
        : null,
      group: urgencyGroup(partial, today),
      searchText: [c.name, c.organization_number, c.contact_person, c.contact_email, c.invoice_email_address, info?.industry_description, info?.municipality]
        .filter((s): s is string => !!s)
        .join(" ")
        .toLowerCase(),
    };
  });
}

export interface CustomerListFilters {
  search: string;
  owner: CustomerListOwnerFilter;
  /** Bruker-id for «Mine kunder». */
  myUserId: string | null;
  status: CustomerStatus | "all";
  counter: CustomerListCounterFilter | null;
}

export function filterCustomerRows(rows: readonly CustomerListRow[], f: CustomerListFilters): CustomerListRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q && !r.searchText.includes(q)) return false;
    if (f.owner === "mine" && !r.owners.some((o) => o.userId === f.myUserId)) return false;
    if (f.owner === "none" && r.owners.length > 0) return false;
    if (f.status !== "all" && r.status !== f.status) return false;
    if (f.counter === "pipe") return r.pipeKr > 0;
    if (f.counter) return r.group === f.counter;
    return true;
  });
}

const byName = (a: CustomerListRow, b: CustomerListRow) => a.name.localeCompare(b.name, "no");

/** Innenfor en gruppe: det som haster mest først; i Rolig GEEK i pipe, så GEEK levert, så navn. */
function compareWithinGroup(a: CustomerListRow, b: CustomerListRow): number {
  switch (a.group) {
    case "overdue":
    case "this_week":
      return (a.nextStep!.dueInDays - b.nextStep!.dueInDays) || byName(a, b);
    case "cooling":
      return (a.coolsInDays! - b.coolsInDays!) || byName(a, b);
    default:
      return (b.pipeKr - a.pipeKr) || ((b.deliveredKr ?? 0) - (a.deliveredKr ?? 0)) || byName(a, b);
  }
}

/** Verdien en kolonne sorteres på; null sorteres alltid sist, uansett retning. */
function sortValue(row: CustomerListRow, key: CustomerListSortKey): number | string | null {
  switch (key) {
    case "name":
      return row.name;
    case "status":
      return row.status ? statusRank(row.status) : null;
    case "next_step":
      return row.nextStep ? row.nextStep.dueInDays : null;
    case "pipe":
      return row.pipeKr;
    case "delivered":
      return row.deliveredKr;
    case "last_contact":
      return row.lastContact ? row.lastContact.daysAgo : null;
  }
}

/**
 * Uten `sort` (default): «Haster mest», gruppe først og det som haster mest
 * innenfor gruppen. Med `sort`: flat liste på kolonnen, rader uten verdi sist.
 */
export function sortCustomerRows(rows: readonly CustomerListRow[], sort: CustomerListSort | null): CustomerListRow[] {
  const list = [...rows];
  if (!sort) {
    return list.sort((a, b) => (URGENCY_ORDER.indexOf(a.group) - URGENCY_ORDER.indexOf(b.group)) || compareWithinGroup(a, b));
  }
  const sign = sort.dir === "asc" ? 1 : -1;
  return list.sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av == null && bv == null) return byName(a, b);
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === "string" ? av.localeCompare(bv as string, "no") : av - (bv as number);
    return cmp * sign || byName(a, b);
  });
}

/** Grupper i hast-rekkefølge, uten tomme. Brukes bare for sortering «Haster mest». */
export function groupCustomerRows(sorted: readonly CustomerListRow[]): Array<{ group: UrgencyGroup; rows: CustomerListRow[] }> {
  return URGENCY_ORDER
    .map((group) => ({ group, rows: sorted.filter((r) => r.group === group) }))
    .filter((g) => g.rows.length > 0);
}

export interface CustomerListCounters {
  overdue: number;
  thisWeek: number;
  cooling: number;
  pipeKr: number;
  pipeOfferCount: number;
  pipeCustomerCount: number;
}

/** Tellerne øverst, regnet på radene i valgt omfang (før tellerfilteret). */
export function customerListCounters(rows: readonly CustomerListRow[]): CustomerListCounters {
  const withPipe = rows.filter((r) => r.pipeKr > 0);
  return {
    overdue: rows.filter((r) => r.group === "overdue").length,
    thisWeek: rows.filter((r) => r.group === "this_week").length,
    cooling: rows.filter((r) => r.group === "cooling").length,
    pipeKr: Math.round(withPipe.reduce((s, r) => s + r.pipeKr, 0)),
    pipeOfferCount: withPipe.reduce((s, r) => s + r.pipeOfferCount, 0),
    pipeCustomerCount: withPipe.length,
  };
}

/** Status-sortering gjenbrukes der lista ikke er gruppert. */
export function statusRank(status: CustomerStatus | null): number {
  return status ? CUSTOMER_STATUS_ORDER[status] : CUSTOMER_STATUS_ORDER.cold + 1;
}

/** «i dag», «i går», «4 d siden», «2 år siden». */
export function formatDaysAgo(days: number): string {
  if (days <= 0) return "i dag";
  if (days === 1) return "i går";
  if (days < 365) return `${days} d siden`;
  const years = Math.floor(days / 365);
  return `${years} år siden`;
}

/** Forfallsteksten under neste steg: «forfalt for 2 dager siden», «i dag», «i morgen», «fredag 23. okt». */
export function describeDue(step: Pick<ListNextStep, "dueDate" | "dueInDays">): string {
  if (step.dueInDays < -1) return `forfalt for ${-step.dueInDays} dager siden`;
  if (step.dueInDays === -1) return "forfalt i går";
  if (step.dueInDays === 0) return "i dag";
  if (step.dueInDays === 1) return "i morgen";
  const weekday = new Date(`${step.dueDate}T12:00:00Z`).toLocaleDateString("nb-NO", { weekday: "long", timeZone: "UTC" });
  return `${weekday} ${formatDateShort(step.dueDate)}`;
}

/** Brønnøysund skriver kommunen i store bokstaver («BERGEN», «MO I RANA»); vis «Bergen», «Mo i Rana». */
export function formatMunicipality(name: string | null): string | null {
  if (!name) return null;
  const small = new Set(["i", "og", "på"]);
  return name
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) => (i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}
